import { useState, useEffect, useCallback, KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { searchPosts, Post } from "@/lib/supabase";
import { Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Highlight matching text in search results
const HighlightMatch = ({ text, query }: { text: string; query: string }) => {
  if (!query.trim() || !text) return <>{text}</>;
  
  // Handle multiple words in the query for better highlighting
  const queryWords = query.trim().toLowerCase().split(/\s+/);
  const parts: { text: string; highlight: boolean }[] = [];
  
  // Create a regex that matches any of the query words
  const regex = new RegExp(`(${queryWords.join('|')})`, 'gi');
  const textParts = text.split(regex);
  
  for (const part of textParts) {
    const shouldHighlight = queryWords.some(word => 
      part.toLowerCase().includes(word.toLowerCase())
    );
    parts.push({ text: part, highlight: shouldHighlight });
  }
  
  return (
    <>
      {parts.map((part, i) => 
        part.highlight ? (
          <span key={i} className="bg-primary/20 text-primary font-medium">
            {part.text}
          </span>
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </>
  );
};

export const SearchDialog = ({ open, onOpenChange }: SearchDialogProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(-1);
  }, [query]);

  // Enhanced search with smarter debounce and ranking
  useEffect(() => {
    const fetchResults = async () => {
      if (query.trim().length < 2) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const searchResults = await searchPosts(query);
        
        // Sort results by relevance (adjust this based on what makes sense for your app)
        const sortedResults = searchResults.sort((a, b) => {
          // Exact title matches get highest priority
          const aExactTitleMatch = a.title.toLowerCase().includes(query.toLowerCase());
          const bExactTitleMatch = b.title.toLowerCase().includes(query.toLowerCase());
          
          if (aExactTitleMatch && !bExactTitleMatch) return -1;
          if (!aExactTitleMatch && bExactTitleMatch) return 1;
          
          // Then sort by view_count for popularity
          return (b.view_count || 0) - (a.view_count || 0);
        });
        
        setResults(sortedResults);
      } catch (error) {
        console.error("Search error:", error);
        toast({
          title: "Search failed",
          description: "Could not retrieve search results. Please try again.",
          variant: "destructive"
        });
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Adaptive debounce - shorter for short queries, longer for longer ones
    const debounceTime = query.length > 3 ? 350 : 200;
    const handler = setTimeout(fetchResults, debounceTime);
    return () => clearTimeout(handler);
  }, [query, toast]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (results.length === 0) return;
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0) {
        handleSelect(results[selectedIndex]);
      } else if (results.length > 0 && query.trim().length >= 2) {
        // If no item is specifically selected but we have results, navigate to first result
        handleSelect(results[0]);
      }
    }
  }, [results, selectedIndex]);

  const handleSelect = (post: Post) => {
    onOpenChange(false);
    navigate(`/post/${post.slug}`);
    // Clear search after navigation
    setQuery("");
  };

  // Create excerpt with more context around the match
  const createContextExcerpt = (content: string, query: string, maxLength = 150) => {
    if (!content) return "";
    
    const plainText = content.replace(/<[^>]*>/g, '');
    const lowerContent = plainText.toLowerCase();
    const queryWords = query.toLowerCase().trim().split(/\s+/);
    
    // Find the best match position (where most query words appear close together)
    let bestMatchIndex = -1;
    let bestMatchCount = 0;
    
    for (let i = 0; i < lowerContent.length; i++) {
      let matchCount = 0;
      for (const word of queryWords) {
        if (lowerContent.substring(i, i + 100).includes(word)) {
          matchCount++;
        }
      }
      
      if (matchCount > bestMatchCount) {
        bestMatchCount = matchCount;
        bestMatchIndex = i;
      }
      
      // Early exit if we found a position with all query words
      if (matchCount === queryWords.length) break;
    }
    
    // If no good match found, just use the beginning
    if (bestMatchIndex === -1) {
      return plainText.substring(0, maxLength) + "...";
    }
    
    // Adjust the context to center around the match
    const startIndex = Math.max(0, bestMatchIndex - 60);
    const endIndex = Math.min(plainText.length, bestMatchIndex + 90);
    
    let excerpt = plainText.substring(startIndex, endIndex);
    if (startIndex > 0) excerpt = "..." + excerpt;
    if (endIndex < plainText.length) excerpt = excerpt + "...";
    
    return excerpt;
  };

  // Handle direct form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (results.length > 0) {
      handleSelect(results[0]);
    } else if (query.trim().length >= 2) {
      toast({
        title: "No results found",
        description: `No posts found matching "${query}"`,
        variant: "default"
      });
    }
  };

  return (
    <CommandDialog 
      open={open} 
      onOpenChange={onOpenChange} 
      className="overflow-hidden backdrop-blur-xl border border-border"
    >
      <form onSubmit={handleSubmit} className="w-full">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <CommandInput
            placeholder="Search posts..."
            value={query}
            onValueChange={setQuery}
            onKeyDown={handleKeyDown}
            className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            autoFocus
          />
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      </form>
      <CommandList className="max-h-[400px] overflow-auto">
        {query.trim().length > 0 && !isLoading && results.length === 0 && (
          <CommandEmpty>No results found for "{query}"</CommandEmpty>
        )}
        {query.trim().length < 2 && query.trim().length > 0 && (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            Type at least 2 characters to search
          </div>
        )}
        {results.length > 0 && (
          <CommandGroup heading="Posts">
            {results.map((post, index) => {
              const contextExcerpt = createContextExcerpt(
                post.excerpt || post.content, 
                query
              );
              
              return (
                <CommandItem 
                  key={post.id} 
                  onSelect={() => handleSelect(post)}
                  className={`${selectedIndex === index ? 'bg-accent text-accent-foreground' : ''} transition-colors cursor-pointer`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">
                      <HighlightMatch text={post.title} query={query} />
                    </span>
                    <span className="text-xs text-muted-foreground truncate max-w-md">
                      <HighlightMatch text={contextExcerpt} query={query} />
                    </span>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export const SearchButton = ({ className }: { className?: string }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    
    // Fix: Use the correct type for document event listener
    document.addEventListener("keydown", down as EventListener);
    return () => document.removeEventListener("keydown", down as EventListener);
  }, []);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={`border-border transition-colors ${className}`}
        onClick={() => setOpen(true)}
      >
        <Search className="h-4 w-4 mr-2" />
        <span className="hidden sm:inline-flex">Search</span>
        <span className="ml-2 hidden sm:inline-flex text-xs text-muted-foreground">
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            ⌘K
          </kbd>
        </span>
      </Button>
      <SearchDialog open={open} onOpenChange={setOpen} />
    </>
  );
};
